import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import Auth from '../../../../api/auth';
import Provision from '../../../../api/provision';
import DeleteFormModal from '../../../../components/DeleteFormModal/DeleteFormModal';
import Chip from '../../../../components/InputFields/Chip';
import OverviewTable from '../../../../components/OverviewTable/OverviewTable';
import useApiQuery from '../../../../hooks/useApiQuery';
import rbacCheck from '../../../../rbac/rbacCheck';

import EditProductFormModal from './EditProductFormModal';
import ForecastTable from './ForecastsTable';
import Summaries from './Summaries';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { JuiceGroupTagsResponse } from '@/types/juicegroupTypes';
import type { Product } from '@/types/productTypes';
import type { Row } from '@/types/tableTypes';

interface OverviewProps {
  product: Product;
  activeResources: boolean;
  onChange: () => void;
}

const Overview: React.FC<OverviewProps> = ({ product, activeResources, onChange }) => {
  const navigate = useNavigate();
  const userData = useUserData();

  const [summariesModalOpened, setSummariesModalOpened] = useState<boolean>(false);
  const [forecastModalOpened, setForecastModalOpened] = useState<boolean>(false);
  const [editModalOpened, setEditModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);

  const { makeApiCall: deleteProductCall } = useApiQuery(
    Auth.deleteV2Product as unknown as APIHookArg,
    {
      successMessage: 'productDeleted',
    }
  );
  const {
    data: juiceTagsProduct,
    error: juiceTagsError,
    makeApiCall: getJuiceGroupCall,
  } = useApiQuery<JuiceGroupTagsResponse>(Provision.fetchJuiceGroupByName);

  const canSeeForecast: boolean = rbacCheck(userData.roles, '/forecast:VIEW') as boolean;
  const canEditProduct: boolean = rbacCheck(userData.roles, '/products:PUT') as boolean;
  const canDeleteProduct: boolean =
    ['compute', 'caasonopenstack', 'caasonbaremetal', 'baremetal'].includes(product.type) &&
    rbacCheck(userData.roles, '/products:DELETE') &&
    !activeResources;

  useEffect(() => {
    if (product.details.juiceGroup) {
      getJuiceGroupCall(product.details.juiceGroup);
    }
  }, [product, getJuiceGroupCall]);

  const deleteProduct = async (): Promise<void> => {
    setDeleteModalOpened(false);
    const data = await deleteProductCall(product._id);

    if (data) {
      if (product.project._id) {
        navigate(`/projects/${product.project._id}`);
      } else if (product.type === 'compute' || product.type === 'evaluation') {
        navigate('/products/compute');
      } else if (product.type === 'caasonopenstack' || product.type === 'caasonbaremetal') {
        navigate('/products/caas-on-openstack');
      } else if (product.type === 'baremetal') {
        navigate('/products/baremetal');
      } else if (product.type === 'safescarf') {
        navigate('/products/safescarf');
      } else {
        navigate('/products');
      }
    }
  };

  const cantDeleteProductMsg: Record<string, string> = {
    caasonopenstack: 'productDeleteDisabledCaasClusters',
    compute: 'productDeleteDisabledOsProjects',
    evaluation: 'productDeleteDisabledOsProjects',
    caasonbaremetal: 'productDeleteDisabledCaasBm',
  };

  const cantDeleteProductMessage = (productType: string): string => {
    if (activeResources) {
      return cantDeleteProductMsg[productType] ?? '';
    }
    if (productType === 'evaluation') {
      return 'productDeleteUndoable';
    }
    return 'productDeleteDisabled';
  };

  const rows = [
    {
      title: 'Description',
      value: product.description,
    },
    {
      title: 'Type',
      value: product.type,
    },
    {
      title: 'Organization',
      component: (
        <Link to={`/governance/organizations/${product.organizationId ?? ''}`}>
          {product.organization?.enterpriseName}
        </Link>
      ),
    },
    {
      title: 'Project',
      component: <Link to={`/projects/${product.project._id ?? ''}`}>{product.project.name}</Link>,
    },
    {
      title: 'Juice Group',
      value: product.details.juiceGroup,
    },
    ...(product.type === 'compute'
      ? [
          {
            title: 'Approved',
            value: product.approved ? 'True' : 'False',
          },
        ]
      : []),
    {
      title: 'Tags',
      component:
        juiceTagsProduct?.status === 404
          ? null
          : juiceTagsProduct?.data.tags.map((tag: string) => (
              <Chip
                key={tag}
                label={tag}
                sx={{ ml: 1 }}
              />
            )),
      error: juiceTagsError,
    },
  ];

  return (
    <>
      {forecastModalOpened && (
        <ForecastTable
          product={product}
          onSubmit={() => {
            setForecastModalOpened(false);
            onChange();
          }}
          onClose={() => {
            setForecastModalOpened(false);
          }}
        />
      )}
      {summariesModalOpened && (
        <Summaries
          juiceGroup={product.details.juiceGroup}
          onClose={() => {
            setSummariesModalOpened(false);
          }}
        />
      )}
      {editModalOpened && (
        <EditProductFormModal
          product={product}
          onSubmit={onChange}
          onCancel={() => {
            setEditModalOpened(false);
          }}
        />
      )}
      {deleteModalOpened && (
        <DeleteFormModal
          fieldName='Product'
          fieldValue={product.name}
          messages={['productDeleteMessage']}
          onSubmit={() => {
            deleteProduct();
          }}
          onCancel={() => {
            setDeleteModalOpened(false);
          }}
        />
      )}
      <OverviewTable
        header={product.name}
        subHeader={
          userData.roles?.includes('ADMIN')
            ? 'Configure the setting for this product.'
            : 'Current product settings (view-only)'
        }
        rows={rows as Row[]}
        editAction={{
          onClick: () => {
            setEditModalOpened(true);
          },
          disabled: !canEditProduct,
          tooltip: 'editSafescarfProductDisabled',
          label: '',
        }}
        deleteAction={{
          onClick: () => {
            setDeleteModalOpened(true);
          },
          disabled: !canDeleteProduct,
          tooltip: cantDeleteProductMessage(product.type),
          label: '',
        }}
        extraActions={
          product.type !== 'caasonbaremetal'
            ? [
                {
                  label: 'Cloud Summaries',
                  onClick: () => {
                    setSummariesModalOpened(true);
                  },
                },
                {
                  label: 'Product Forecast',
                  onClick: () => {
                    setForecastModalOpened(true);
                  },
                  disabled: !canSeeForecast,
                },
              ]
            : []
        }
      />
    </>
  );
};

export default Overview;
